/**
 * Flow Optimizer formula — AST <-> FormulaBlockNode bridge
 * For Media Devoted | Created By Ciro Autiero
 *
 * The evaluator AST uses `ident` for any identifier; the FE block-builder
 * uses an explicitly-tagged `metric` node. These helpers translate between
 * them so the same parser can seed visual blocks.
 */

import { parseFormula } from "./ast";
import type { AstNode } from "./ast";
import { serializeFormula } from "./serializer";
import type { FormulaBlockNode } from "../voluum-flow-optimizer.types";
import {
  isSubformulaRefName,
  makeSubformulaRefName,
  readSubformulaRefId,
} from "../voluum-flow-optimizer.types";

export function astToBlock(node: AstNode): FormulaBlockNode {
  if (node.kind === "num") return { kind: "num", value: node.value };
  if (node.kind === "ident") {
    const subId = readSubformulaRefId(node.name);
    if (subId) return { kind: "subref", subId };
    return { kind: "metric", metric: node.name };
  }
  if (node.kind === "unary") {
    return { kind: "unary", op: "-", operand: astToBlock(node.operand) };
  }
  if (node.kind === "call") {
    const fn = node.name === "ln" ? "log" : node.name;
    return { kind: "call", fn, args: node.args.map(astToBlock) };
  }
  return {
    kind: "binary",
    op: node.op,
    left: astToBlock(node.left),
    right: astToBlock(node.right),
  };
}

export function blockToAst(node: FormulaBlockNode): AstNode {
  if (node.kind === "num") return { kind: "num", value: node.value };
  if (node.kind === "metric") return { kind: "ident", name: node.metric };
  if (node.kind === "subref") {
    return { kind: "ident", name: makeSubformulaRefName(node.subId) };
  }
  if (node.kind === "unary") {
    return { kind: "unary", op: "-", operand: blockToAst(node.operand) };
  }
  if (node.kind === "call") {
    return {
      kind: "call",
      name: node.fn,
      args: node.args.map(blockToAst),
    };
  }
  return {
    kind: "binary",
    op: node.op,
    left: blockToAst(node.left),
    right: blockToAst(node.right),
  };
}

/** Re-export to keep existing callers' import surface unchanged. */
export { isSubformulaRefName, readSubformulaRefId, makeSubformulaRefName };

/** Parse `expression` into a FormulaBlockNode. Returns null if parse fails. */
export function parseExpressionToBlock(
  expression: string,
): FormulaBlockNode | null {
  const trimmed = expression?.trim();
  if (!trimmed) return null;
  try {
    return astToBlock(parseFormula(trimmed));
  } catch {
    return null;
  }
}

/** Serialize a FormulaBlockNode back to canonical text via the AST serializer. */
export function serializeBlock(node: FormulaBlockNode | null): string {
  if (!node) return "";
  return serializeFormula(blockToAst(node));
}
